import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  CircularProgress,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Alert,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Inventory as InventoryIcon,
  Block as BlockIcon,
  FileDownload as FileDownloadIcon,
  GetApp as GetAppIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useDevicesApi } from "../utlis/useDevicesApi";
import { useTheme } from "../utlis/useTheme";
import { useNotification } from "../context/NotificationContext";
import axiosInstance from "../utlis/axiosInstance";
import { format } from "date-fns";
import axios from "axios";

interface Device {
  id: number;
  name: string;
  unique_id: string;
  status: string;
  phone: string;
  model: string;
  category: string;
  disabled?: boolean;
  inventory?: boolean;
  purchase_date?: string;
  warranty_expiry?: string;
  sim_number?: string;
  last_maintenance_date?: string;
  attributes: {
    ignition: boolean;
    charge: boolean;
    blocked: boolean;
    battery_level: string;
    status: string;
    device_online: boolean;
    latest_speed?: number;
    last_updated_at?: number;
    mdn_number?: string;
    customer_name?: string;
  };
}

// CSV Export Utility Functions
const convertToCSV = (data: Device[]): string => {
  if (data.length === 0) return "";

  const headers = [
    "Device Name",
    "Model",
    "Device ID",
    "SIM Number",
    "Category",
    "Status",
    "Purchase Date",
    "Warranty Expiry",
    "Last Maintenance",
    "Ignition",
    "Charge",
    "Blocked",
    "Battery Level",
    "Device Online",
    "Latest Speed",
    "MDN Number",
    "Customer Name",
    "Last Updated",
  ];

  const csvRows = [headers.join(",")];

  data.forEach((device) => {
    const row = [
      `"${device.name || ""}"`,
      `"${device.model || ""}"`,
      `"${device.unique_id || ""}"`,
      `"${device.phone || "N/A"}"`,
      `"${device.category || ""}"`,
      `"${device.status || ""}"`,
      `"${device.purchase_date || "N/A"}"`,
      `"${device.warranty_expiry || "N/A"}"`,
      `"${device.last_maintenance_date || "N/A"}"`,
      `"${device.attributes?.ignition ? "Yes" : "No"}"`,
      `"${device.attributes?.charge ? "Yes" : "No"}"`,
      `"${device.attributes?.blocked ? "Yes" : "No"}"`,
      `"${device.attributes?.battery_level || "N/A"}"`,
      `"${device.attributes?.device_online ? "Online" : "Offline"}"`,
      `"${device.attributes?.latest_speed || "N/A"}"`,
      `"${device.attributes?.mdn_number || "N/A"}"`,
      `"${device.attributes?.customer_name || "N/A"}"`,
      `"${device.attributes?.last_updated_at ? format(new Date(device.attributes.last_updated_at), "dd:MM:yyyy") : "N/A"}"`,
    ];
    csvRows.push(row.join(","));
  });

  return csvRows.join("\n");
};

const downloadCSV = (csvContent: string, filename: string): void => {
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const Inventory: React.FC = () => {
  // const { devices, loading, refreshDevices } = useDevicesApi<Device>();
  const { getPrimaryColor, getPrimaryLightColor, getPrimaryDarkColor } =
    useTheme();
  const { showNotification } = useNotification();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState<Device | null>(null);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);

  // Handle page change
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  async function getAllDevices() {
    const response = await axiosInstance.get(
      "/gpstracker/devicehandler/?limit=10000&offset=0",
    );
    setDevices(response.data.responseData);
    setLoading(false);
  }

  useEffect(() => {
    getAllDevices();
  }, []);

  // Handle rows per page change
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Export handlers
  const handleBatchExport = (batch: {
    key: number | "no-ts";
    items: Device[];
  }) => {
    const timestamp =
      batch.key === "no-ts"
        ? "no-timestamp"
        : formatFromMinuteKey(batch.key as number).replace(/[: ]/g, "-");
    const filename = `inventory-batch-${timestamp}-${batch.items.length}-devices.csv`;
    const csvContent = convertToCSV(batch.items);
    downloadCSV(csvContent, filename);
  };

  const handleCompleteExport = () => {
    const timestamp = new Date().toISOString().split("T")[0];
    const filename = `inventory-complete-${timestamp}-${filteredDevices.length}-devices.csv`;
    const csvContent = convertToCSV(filteredDevices);
    downloadCSV(csvContent, filename);
  };

  // Delete handlers
  const handleDeleteClick = (device: Device) => {
    setDeviceToDelete(device);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deviceToDelete) return;

    try {
      setDeleting(true);
      const response = await axiosInstance.patch(
        `/gpstracker/devicehandler/${deviceToDelete.id}`,
        {
          delete: true,
        },
      );

      if (response.data.status) {
        showNotification(
          "Device Disabled",
          `Device "${deviceToDelete.name}" has been successfully disabled and removed from inventory.`,
          "success",
        );
        // await refreshDevices();
      } else {
        showNotification(
          "Delete Failed",
          response.data.message ||
            "Failed to disable device. Please try again.",
          "error",
        );
      }
    } catch (err: any) {
      console.error("Error disabling device:", err);
      showNotification(
        "Delete Failed",
        "An error occurred while disabling the device. Please try again.",
        "error",
      );
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setDeviceToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setDeviceToDelete(null);
  };

  // Filter devices based on inventory flag and search query
  const filteredDevices = devices
    .filter((device: Device) => device.inventory === true)
    .filter(
      (device: Device) =>
        device.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        device.model.toLowerCase().includes(searchQuery.toLowerCase()) ||
        device.unique_id.toLowerCase().includes(searchQuery.toLowerCase()),
    );

  // Group by identical attributes.last_updated_at at minute precision (ignore seconds)
  const groupedBatches = useMemo(() => {
    const groups = new Map<number | "no-ts", Device[]>();

    for (const d of filteredDevices) {
      const ts = d.attributes?.last_updated_at;
      const key: number | "no-ts" =
        typeof ts === "number" && !Number.isNaN(ts)
          ? Math.floor(ts / 60000) // minute epoch key
          : "no-ts";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(d);
    }

    // Sort keys: timestamp batches desc, 'no-ts' at bottom
    const sortedEntries: Array<{ key: number | "no-ts"; items: Device[] }> =
      Array.from(groups.entries())
        .map(([key, items]) => ({ key, items }))
        .sort((a, b) => {
          if (a.key === "no-ts" && b.key === "no-ts") return 0;
          if (a.key === "no-ts") return 1;
          if (b.key === "no-ts") return -1;
          return (b.key as number) - (a.key as number);
        });

    return sortedEntries;
  }, [filteredDevices]);

  // Format timestamp util
  const pad2 = (n: number) => (n < 10 ? `0${n}` : `${n}`);
  const formatFromMinuteKey = (minuteKey?: number) => {
    if (
      minuteKey === undefined ||
      minuteKey === null ||
      Number.isNaN(minuteKey)
    )
      return "No timestamp";
    try {
      const d = new Date(minuteKey * 60000);
      const yyyy = d.getFullYear();
      const mm = pad2(d.getMonth() + 1);
      const dd = pad2(d.getDate());
      const hh = pad2(d.getHours());
      const mi = pad2(d.getMinutes());
      return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
    } catch {
      return "Invalid date";
    }
  };

  // Batch color palette (cycled)
  const batchColors = [
    "#E3F2FD", // light blue
    "#E8F5E9", // light green
    "#FFF3E0", // light orange
    "#F3E5F5", // light purple
    "#FBE9E7", // light red/orange
    "#E0F7FA", // cyan
  ];

  // Get status chip color (kept for future use)
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "active":
        return {
          color: "#43A047",
          bgcolor: alpha("#43A047", 0.1),
          borderColor: alpha("#43A047", 0.3),
        };
      case "inactive":
        return {
          color: "#E53935",
          bgcolor: alpha("#E53935", 0.1),
          borderColor: alpha("#E53935", 0.3),
        };
      case "maintenance":
        return {
          color: "#FB8C00",
          bgcolor: alpha("#FB8C00", 0.1),
          borderColor: alpha("#FB8C00", 0.3),
        };
      default:
        return {
          color: "#757575",
          bgcolor: alpha("#757575", 0.1),
          borderColor: alpha("#757575", 0.3),
        };
    }
  };

  // Pagination over batches (not individual devices) for clarity when batching
  const paginatedBatches = useMemo(() => {
    if (rowsPerPage <= 0) return groupedBatches;
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return groupedBatches.slice(start, end);
  }, [groupedBatches, page, rowsPerPage]);

  if (loading && devices.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: "#f8fafc", minHeight: "100vh", py: 4 }}>
      <Container maxWidth="xl">
        {/* Page Header */}
        <Box
          sx={{
            mb: 4,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", md: "center" },
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                background: `linear-gradient(135deg, ${getPrimaryColor()} 0%, ${getPrimaryLightColor()} 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                mb: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <InventoryIcon sx={{ color: getPrimaryColor() }} /> Device
              Inventory
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage and track your device inventory
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.5 }}
            >
              Batches are grouped by identical Last Updated timestamps.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Button
              variant="outlined"
              startIcon={<GetAppIcon />}
              onClick={handleCompleteExport}
              disabled={filteredDevices.length === 0}
              sx={{
                borderColor: getPrimaryColor(),
                color: getPrimaryColor(),
                borderRadius: 1,
                px: 2.5,
                py: 1,
                fontSize: "0.75rem",
                fontWeight: 500,
                textTransform: "none",
                "&:hover": {
                  borderColor: getPrimaryLightColor(),
                  bgcolor: alpha(getPrimaryColor(), 0.05),
                  transform: "translateY(-1px)",
                },
                transition: "all 0.2s ease",
                "&:disabled": {
                  borderColor: alpha(getPrimaryColor(), 0.3),
                  color: alpha(getPrimaryColor(), 0.3),
                },
              }}
            >
              Export All ({filteredDevices.length})
            </Button>
            <Button
              variant="contained"
              onClick={() => navigate("/bulk-add")}
              sx={{
                bgcolor: getPrimaryColor(),
                borderRadius: 1,
                px: 2.5,
                py: 1,
                fontSize: "0.75rem",
                fontWeight: 500,
                textTransform: "none",
                boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                "&:hover": {
                  bgcolor: getPrimaryLightColor(),
                  boxShadow: `0 0 8px ${alpha(getPrimaryColor(), 0.5)}`,
                  transform: "translateY(-1px)",
                },
                transition: "all 0.2s ease",
              }}
            >
              Bulk
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/add-device-inventory")}
              sx={{
                bgcolor: getPrimaryColor(),
                borderRadius: 1,
                px: 2.5,
                py: 1,
                fontSize: "0.75rem",
                fontWeight: 500,
                textTransform: "none",
                boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                "&:hover": {
                  bgcolor: getPrimaryLightColor(),
                  boxShadow: `0 0 8px ${alpha(getPrimaryColor(), 0.5)}`,
                  transform: "translateY(-1px)",
                },
                transition: "all 0.2s ease",
              }}
            >
              Add New Item
            </Button>
          </Box>
        </Box>

        {/* Search Bar */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 4,
            bgcolor: "white",
            border: "1px solid",
            borderColor: alpha(getPrimaryColor(), 0.2),
          }}
        >
          <TextField
            fullWidth
            placeholder="Search by device name, model, or ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#6b7280" }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setSearchQuery("")}
                    size="small"
                    sx={{
                      "&:hover": {
                        backgroundColor: alpha("#6b7280", 0.1),
                      },
                    }}
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 3,
                bgcolor: "#f9fafb",
                transition: "all 0.2s ease",
                "&:hover": {
                  bgcolor: "#f3f4f6",
                },
                "&.Mui-focused": {
                  bgcolor: "white",
                  boxShadow: `0 0 0 2px ${alpha(getPrimaryColor(), 0.2)}`,
                },
              },
            }}
          />
        </Paper>

        {/* Inventory Table (grouped by batch) */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: "1px solid",
            borderColor: alpha(getPrimaryColor(), 0.2),
            overflow: "hidden",
          }}
        >
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(getPrimaryColor(), 0.05) }}>
                  <TableCell sx={{ fontWeight: 600 }}>Device Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Model</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>SIM</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedBatches.map((batch, idx) => {
                  const color = batchColors[idx % batchColors.length];
                  const isNoTs = batch.key === "no-ts";
                  const headerText = isNoTs
                    ? `Batch: No timestamp`
                    : `Batch: ${formatFromMinuteKey(batch.key as number)}`;
                  return (
                    <React.Fragment key={`${String(batch.key)}-${idx}`}>
                      {/* Batch header row */}
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          sx={{
                            bgcolor: alpha(color, 0.6),
                            borderTop: `1px solid ${alpha(getPrimaryColor(), 0.15)}`,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              fontWeight={700}
                              color="#111827"
                            >
                              {headerText}
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="#374151"
                                fontWeight={600}
                              >
                                Count: {batch.items.length}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => handleBatchExport(batch)}
                                sx={{
                                  color: getPrimaryColor(),
                                  bgcolor: alpha(getPrimaryColor(), 0.1),
                                  "&:hover": {
                                    bgcolor: alpha(getPrimaryColor(), 0.2),
                                    transform: "scale(1.05)",
                                  },
                                  transition: "all 0.2s ease",
                                }}
                                title={`Export batch with ${batch.items.length} devices`}
                              >
                                <FileDownloadIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        </TableCell>
                      </TableRow>

                      {/* Devices within batch */}
                      {batch.items.map((device: Device) => (
                        <TableRow
                          key={device.id}
                          sx={{
                            "&:hover": {
                              bgcolor: alpha(color, 0.25),
                            },
                            backgroundColor: alpha(color, 0.15),
                          }}
                        >
                          <TableCell>{device.name}</TableCell>
                          <TableCell>{device.model}</TableCell>
                          <TableCell>{device.unique_id}</TableCell>
                          <TableCell>{device.sim_number || "N/A"}</TableCell>
                          <TableCell>{device.phone || "N/A"}</TableCell>
                          <TableCell>{device.category}</TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  navigate(`/edit-device/${device.id}`)
                                }
                                sx={{
                                  color: getPrimaryColor(),
                                  "&:hover": {
                                    bgcolor: alpha(getPrimaryColor(), 0.1),
                                  },
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteClick(device)}
                                sx={{
                                  color: "#E53935",
                                  "&:hover": {
                                    bgcolor: alpha("#E53935", 0.1),
                                    transform: "scale(1.05)",
                                  },
                                  transition: "all 0.2s ease",
                                }}
                                title="Delete device from inventory"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  );
                })}

                {groupedBatches.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                      <Typography
                        variant="h6"
                        color="text.secondary"
                        gutterBottom
                      >
                        No inventory items found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {searchQuery
                          ? `No items match your search "${searchQuery}"`
                          : "Try adding some inventory items"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={groupedBatches.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleCancelDelete}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
            },
          }}
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  backgroundColor: alpha("#E53935", 0.12),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <WarningIcon sx={{ color: "#E53935", fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={600} color="error.main">
                  Delete Device
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This action cannot be undone
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This will disable the device and remove it from inventory. The
              device will no longer be tracked.
            </Alert>
            <Typography>
              Are you sure you want to delete device{" "}
              <strong>"{deviceToDelete?.name}"</strong>?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Device ID: {deviceToDelete?.unique_id}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2, bgcolor: alpha("#E53935", 0.05) }}>
            <Button
              onClick={handleCancelDelete}
              disabled={deleting}
              sx={{ color: "text.secondary" }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              disabled={deleting}
              color="error"
              variant="contained"
              startIcon={
                deleting ? <CircularProgress size={16} /> : <DeleteIcon />
              }
              sx={{
                bgcolor: "#E53935",
                "&:hover": { bgcolor: "#C62828" },
                "&:disabled": { bgcolor: alpha("#E53935", 0.3) },
              }}
            >
              {deleting ? "Deleting..." : "Delete Device"}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default Inventory;
